import type { AutomationSignals, ChromeObjectSignals } from '@fraudeai/schema';
import { defineModule, type ModuleContext } from '../harness.js';
import { attempt, attemptAsync, delay } from '../util/safe.js';

/**
 * Признаки автоматизации браузера.
 *
 * Драйверы оставляют следы трёх видов: явные флаги (`navigator.webdriver`),
 * служебные объекты в глобальной области (`cdc_…`, `__playwright…`) и
 * несоответствия окружения, возникающие из-за отсутствия настоящего UI —
 * нулевой размер окна, пустой список плагинов, странное состояние разрешений.
 *
 * Ни один признак по отдельности не доказателен: `webdriver` отключают одной
 * строкой, глобалы переименовывают. Ценность в совокупности.
 */

/** Служебные имена, которые оставляют драйверы Selenium/WebDriver. */
const DRIVER_ARTIFACT_PATTERNS: RegExp[] = [
  /^[$_]{0,2}cdc_/i,
  /^[$_]{0,2}wdc_/i,
  /^[$_]{0,2}selenium/i,
  /^[$_]{0,2}webdriver/i,
  /^[$_]{0,2}fxdriver/i,
  /^[$_]{0,2}driver_(un)?wrapped$/i,
  /^__webdriver_/i,
  /^__selenium_/i,
  /^__lastWatirAlert$/i,
  /^__lastWatirConfirm$/i,
  /^__lastWatirPrompt$/i,
  /^_Selenium_IDE_Recorder$/i,
  /^calledSelenium$/i,
  /^\$chrome_asyncScriptInfo$/i,
  /^__\$webdriverAsyncExecutor$/i,
  /^domAutomation(Controller)?$/i,
];

/** Глобалы фреймворков автоматизации и headless-обёрток. */
const AUTOMATION_GLOBALS = [
  '__nightmare',
  '_phantom',
  'phantom',
  'callPhantom',
  '__phantomas',
  '__playwright',
  '__pw_manual',
  '__pwInitScripts',
  '__puppeteer_evaluation_script__',
  'Buffer',
  'emit',
  'spawn',
  '__stopAllActiveAnimations',
  '__generateAllPaintWorkletState',
  'awesomium',
  'domAutomationController',
];

const HEADLESS_UA = /HeadlessChrome|PhantomJS|SlimerJS|Nightmare|Electron|jsdom|Puppeteer|Playwright/i;

/**
 * Детект активного CDP-сеанса.
 *
 * Когда включён `Runtime.enable` (а его включают Puppeteer, Playwright и
 * открытые DevTools), объекты, попадающие в console, сериализуются для
 * инспектора. Сериализация читает `stack` у объектов-ошибок — если подменить
 * это свойство геттером, факт чтения станет видимым.
 *
 * Честная оговорка: открытые DevTools дают тот же результат. Поэтому сигнал
 * трактуется как «CDP или DevTools», а не как доказательство автоматизации.
 */
function detectCdp(): { detected: boolean; method: string | null } {
  const result = attempt(() => {
    let read = false;
    const probe = new Error('fraudeai');
    Object.defineProperty(probe, 'stack', {
      configurable: true,
      get() {
        read = true;
        return '';
      },
    });
    // debug тише остальных уровней и не засоряет консоль пользователя.
    console.debug(probe);
    return read;
  }, false);

  return result ? { detected: true, method: 'error-stack-getter' } : { detected: false, method: null };
}

function scanGlobalNames(): string[] {
  return (
    attempt(() => {
      const names = new Set<string>();
      for (const key of Object.getOwnPropertyNames(globalThis)) {
        if (DRIVER_ARTIFACT_PATTERNS.some((p) => p.test(key))) names.add(`window.${key}`);
      }
      if (typeof document !== 'undefined') {
        for (const key of Object.getOwnPropertyNames(document)) {
          if (DRIVER_ARTIFACT_PATTERNS.some((p) => p.test(key))) names.add(`document.${key}`);
        }
      }
      return [...names];
    }, []) ?? []
  );
}

function scanDocumentAttributes(): string[] {
  return (
    attempt(() => {
      if (typeof document === 'undefined') return [];
      const suspicious = /webdriver|selenium|driver|automation/i;
      const out: string[] = [];
      for (const el of [document.documentElement, document.body]) {
        if (!el) continue;
        for (const name of el.getAttributeNames()) {
          if (suspicious.test(name)) out.push(`${el.tagName.toLowerCase()}[${name}]`);
        }
      }
      return out;
    }, []) ?? []
  );
}

/**
 * Форма объекта `window.chrome`.
 *
 * У настольного Chromium там лежат служебные функции — `csi`, `loadTimes`,
 * `runtime`. Кастомные сборки антидетектов ведут себя одним из двух
 * способов, и оба заметны: либо теряют объект целиком, и получается Chrome
 * без `window.chrome`, либо переопределяют его через defineProperty — и
 * функции внутри перестают выглядеть нативными.
 *
 * Проверка нативности здесь та же, что в модуле целостности: настоящая
 * встроенная функция печатается как `[native code]` и не имеет собственного
 * свойства `prototype`.
 */
function collectChromeObject(): ChromeObjectSignals | null {
  return attempt(() => {
    const chrome = (globalThis as { chrome?: Record<string, unknown> }).chrome;
    if (!chrome || typeof chrome !== 'object') {
      return {
        present: false,
        keys: [],
        hasCsi: false,
        hasLoadTimes: false,
        hasRuntime: false,
        nativeFunctions: false,
      };
    }

    const looksNative = (value: unknown): boolean =>
      attempt(() => {
        if (typeof value !== 'function') return false;
        const source = Function.prototype.toString.call(value);
        if (!/\{\s*\[native code\]\s*\}/.test(source)) return false;
        // У встроенных методов нет собственного prototype — есть у обычных.
        return !Object.getOwnPropertyNames(value).includes('prototype');
      }, false) ?? false;

    const csi = chrome['csi'];
    const loadTimes = chrome['loadTimes'];

    return {
      present: true,
      keys: attempt(() => Object.keys(chrome), []) ?? [],
      hasCsi: typeof csi === 'function',
      hasLoadTimes: typeof loadTimes === 'function',
      hasRuntime: Boolean(chrome['runtime']),
      // Обе функции должны выглядеть нативными; достаточно одной
      // подделанной, чтобы признак не выполнялся.
      nativeFunctions:
        (typeof csi !== 'function' || looksNative(csi)) &&
        (typeof loadTimes !== 'function' || looksNative(loadTimes)) &&
        (typeof csi === 'function' || typeof loadTimes === 'function'),
    };
  });
}

/** Окно занимает экран целиком — признак полноэкранного режима. */
function looksFullscreen(outerHeight: number): boolean {
  return (
    attempt(() => {
      if (matchMedia('(display-mode: fullscreen)').matches) return true;
      // Запас в пару пикселей: на некоторых системах высота окна в
      // полноэкранном режиме отличается от высоты экрана на единицы.
      return Math.abs(screen.height - outerHeight) <= 2;
    }, false) ?? false
  );
}

export const automationModule = defineModule({
  id: 'automation',
  phase: 1,
  timeoutMs: 4000,
  async run(ctx: ModuleContext): Promise<AutomationSignals> {
    const parsed = ctx.signals.navigator?.parsed ?? null;
    const isBlinkDesktop =
      parsed?.engine === 'Blink' && !parsed.isMobile && parsed.platformFamily !== 'android';

    const cdp = detectCdp();
    const driverArtifacts = scanGlobalNames();
    const documentAttributes = scanDocumentAttributes();

    const automationGlobals = (
      attempt(() => AUTOMATION_GLOBALS.filter((name) => name in globalThis), []) ?? []
    ).filter((name) =>
      // Buffer/emit/spawn встречаются и в обычных сборках со сборщиком,
      // поэтому засчитываем их только вместе с другими признаками.
      ['Buffer', 'emit', 'spawn'].includes(name) ? driverArtifacts.length > 0 : true,
    );

    const userAgent = ctx.signals.navigator?.userAgent ?? '';
    const uaDeclaresHeadless = HEADLESS_UA.test(userAgent);

    const headlessHints: string[] = [];

    const outer = attempt(() => ({ w: outerWidth, h: outerHeight }));
    const inner = attempt(() => ({ w: innerWidth, h: innerHeight }));
    let windowGeometrySuspicious = false;

    if (outer && inner) {
      if (outer.w === 0 || outer.h === 0) {
        headlessHints.push('нулевой размер внешнего окна');
        windowGeometrySuspicious = true;
      } else if (outer.h === inner.h && !parsed?.isMobile && !looksFullscreen(outer.h)) {
        // У десктопного окна есть вкладки и адресная строка: внешняя высота
        // всегда больше внутренней на несколько десятков пикселей.
        //
        // Кроме полноэкранного режима — там интерфейс убран, и высоты
        // совпадают у совершенно обычного браузера. На живом Chrome под
        // macOS это давало обвинение в безоконном запуске, поэтому режим
        // распознаётся отдельно и проверка в нём не применяется.
        headlessHints.push('высота окна равна высоте вьюпорта (нет интерфейса браузера)');
        windowGeometrySuspicious = true;
      }
      if (inner.w > outer.w || inner.h > outer.h) {
        headlessHints.push('вьюпорт больше окна');
        windowGeometrySuspicious = true;
      }
    }

    if (isBlinkDesktop) {
      if (!('chrome' in globalThis)) headlessHints.push('нет объекта window.chrome в Blink');
      const pluginCount = ctx.signals.navigator?.plugins?.length ?? null;
      if (pluginCount === 0) headlessHints.push('пустой список плагинов в десктопном Chrome');
      if (!('connection' in navigator)) headlessHints.push('нет navigator.connection в Chrome');
    }

    if ((ctx.signals.navigator?.languages?.length ?? 1) === 0) {
      headlessHints.push('пустой navigator.languages');
    }

    /**
     * Классическая аномалия headless-Chrome: Notification.permission
     * возвращает 'denied', тогда как Permissions API для того же разрешения
     * сообщает 'prompt'. В обычном браузере эти два источника согласованы.
     */
    const permissionQuirk =
      (await attemptAsync(async () => {
        const notification = (globalThis as { Notification?: { permission: string } }).Notification;
        if (!notification || !navigator.permissions?.query) return false;
        const status = await navigator.permissions.query({ name: 'notifications' as PermissionName });
        return notification.permission === 'denied' && status.state === 'prompt';
      }, false)) ?? false;

    if (permissionQuirk) headlessHints.push('рассогласование Notification.permission и Permissions API');

    // Небольшая пауза: часть драйверов дописывает свои объекты не сразу.
    await delay(0);

    const webdriver = ctx.signals.navigator?.webdriver ?? attempt(() => navigator.webdriver);

    let score = 0;
    if (webdriver === true) score += 45;
    if (uaDeclaresHeadless) score += 40;
    if (driverArtifacts.length > 0) score += 35;
    if (automationGlobals.length > 0) score += 25;
    if (documentAttributes.length > 0) score += 25;
    if (cdp.detected) score += 15;
    score += Math.min(headlessHints.length * 8, 24);

    return {
      webdriver,
      chromeObject: collectChromeObject(),
      cdpDetected: cdp.detected,
      cdpMethod: cdp.method,
      driverArtifacts,
      automationGlobals,
      documentAttributes,
      headlessHints,
      uaDeclaresHeadless,
      windowGeometrySuspicious,
      permissionQuirk,
      score: Math.min(score, 100),
    };
  },
});
